import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { ProjectRenderer } from './ProjectRenderer';
import { OrbitControls } from './controls/OrbitControls';
import { debounce } from 'lodash';
import { ThreeEvent } from './types';
import gsap from 'gsap';
import * as THREE from 'three';

export class CameraController {
	camera!: THREE.PerspectiveCamera;
	screenshotCamera!: THREE.PerspectiveCamera;
	controls!: OrbitControls;

	constructor(private renderer: ProjectRenderer) {
		this.init();
	}

	private init() {
		this.camera = new THREE.PerspectiveCamera();
		this.screenshotCamera = new THREE.PerspectiveCamera();

		this.camera.position.set(5, 5, 5);
		this.camera.layers.enableAll();
		this.screenshotCamera.layers.enableAll();

		this.camera.aspect =
			this.renderer.canvas!.clientWidth / this.renderer.canvas!.clientHeight;
		this.screenshotCamera.aspect = window.innerWidth / window.innerHeight;
		this.camera.updateProjectionMatrix();
		this.screenshotCamera.updateProjectionMatrix();

		this.createControls();
	}

	private createControls() {
		this.controls = new OrbitControls(this.camera, this.renderer.canvas);
		this.controls.panSpeed = RENDERER_CONFIG.CONTROLS.PAN_SPEED;
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.05;

		this.controls.minDistance = RENDERER_CONFIG.CONTROLS.MIN_DISTANCE;
		this.controls.maxDistance = RENDERER_CONFIG.CONTROLS.MAX_DISTANCE;

		this.controls.maxPolarAngle = THREE.MathUtils.degToRad(
			RENDERER_CONFIG.CONTROLS.MAX_POLAR_ANGLE
		);

		const updateStateDistance = () => {
			const distance = this.camera.position.distanceTo(this.controls.target);
			this.renderer.emitter.emit(ThreeEvent.UPDATE_CAMERA_DISTANCE, distance);
		};

		updateStateDistance();

		this.controls.addEventListener(
			'change',
			debounce(updateStateDistance, 150)
		);

		this.controls.saveState();
	}

	prepareScreenshotCamera() {
		const box = new THREE.Box3().setFromObject(this.renderer.collection);
		const center = box.getCenter(new THREE.Vector3());
		const size = box.getSize(new THREE.Vector3());
		this.screenshotCamera.position.set(
			0,
			size.length() * 1.2,
			size.length() * 1.2
		);

		this.screenshotCamera.lookAt(center);
		this.screenshotCamera.updateProjectionMatrix();
	}

	centerCamera() {
		this.controls.enabled = false;

		const box = new THREE.Box3().setFromObject(this.renderer.collection);
		const size = new THREE.Vector3();
		box.getSize(size);

		const center = box.getCenter(new THREE.Vector3());

		this.controls.target.set(center.x, center.y, center.z);
		this.camera.lookAt(box.getCenter(new THREE.Vector3()));
		this.controls.update();
		const width = Math.abs(box.max.x - box.min.x),
			height = Math.abs(box.max.y - box.min.y),
			depth = Math.abs(box.max.z - box.min.z);

		const x = width / 2 + 1,
			y = width + 1,
			z = depth + 2;

		gsap.to(this.controls.target, {
			ease: 'power2.out',
			duration: 1,
			x: center.x,
			y: center.y,
			z: center.z,
		});

		gsap.to(this.camera.position, {
			ease: 'power2.out',
			duration: 1,
			x,
			y,
			z,
			onComplete: () => {
				this.controls.enabled = true;
				this.controls.update();
			},
		});
	}

	setDistanceToTarget(distance: number) {
		const target = this.controls.target.clone();
		const currentPosition = this.camera.position.clone();
		const direction = new THREE.Vector3()
			.subVectors(currentPosition, target)
			.normalize();

		const newPosition = target
			.clone()
			.add(direction.clone().multiplyScalar(distance));

		gsap.to(this.camera.position, {
			x: newPosition.x,
			y: newPosition.y,
			z: newPosition.z,
			duration: 0.5,
			ease: 'power1.inOut',
			onUpdate: () => {
				this.camera.lookAt(target);
			},
			onComplete: () => {
				this.controls.update();
			},
		});
	}

	handleResize(aspect: number) {
		this.camera.aspect = aspect;
		this.camera.updateProjectionMatrix();
	}

	dispose() {
		this.controls.dispose();
	}
}
